import { useCallback, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { Box, List, ListItem } from '@mui/material'
import {
  Typography,
  Alert,
  Button,
  Dialog,
  DialogFooter,
  DialogContent,
  DialogHeader
} from '@carto/meridian-ds/components'
import { useAppDispatch, useAppSelector } from 'app/state'
import { RequestStatus } from 'features/common'
import { duplicateMapAction } from 'features/maps/state/duplicateMapSlice'
import { setDryRunDuplicateMapStatus, setDuplicateMapError, setDuplicateMapStatus } from 'features/maps/state/mapsSlice'

function PartiallyDuplicateMapDialog({
  mapId,
  isOpen,
  onClose
}: {
  mapId: string
  isOpen: boolean
  onClose: () => void
}) {
  const dispatch = useAppDispatch()
  const isDuplicating = useAppSelector((state) => state.maps.duplicateMapStatus === RequestStatus.Pending)
  const isFinished = useAppSelector((state) => state.maps.duplicateMapStatus === RequestStatus.Fulfilled)
  const unavailableConnections = useAppSelector((state) => state.maps.unavailableConnections)

  const handleClose = useCallback(() => {
    dispatch(setDuplicateMapError(null))
    dispatch(setDryRunDuplicateMapStatus(RequestStatus.Idle))
    dispatch(setDuplicateMapStatus(RequestStatus.Idle))
    onClose()
  }, [dispatch, onClose])

  useEffect(() => {
    if (isOpen && isFinished) handleClose()
  }, [isOpen, isFinished, handleClose])

  const duplicateMap = () => {
    dispatch(duplicateMapAction({ mapId }))
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} size="small" aria-label="duplicate-map-dialog">
      <DialogHeader
        aria-label="duplicate-map-dialog-title"
        title={<FormattedMessage id="builder.actions.duplicate.dialog.title" />}
        onClose={onClose}
      />

      <DialogContent>
        <Typography component="span" variant="body2" color="textSecondary">
          <FormattedMessage
            id="builder.actions.duplicate.dialog.content"
            values={{
              strong: (content) => <strong>{content}</strong>
            }}
          />
        </Typography>

        <Alert severity="warning">
          <FormattedMessage id="builder.actions.duplicate.dialog.conflictDetails" />
          {unavailableConnections && (
            <Box mt={1}>
              <List disablePadding={true} dense={true}>
                {unavailableConnections.map((connection, i) => (
                  <ListItem key={i} disableGutters={true} dense={true}>
                    {connection.name} (
                    <FormattedMessage id={`data-explorer.providers.${connection.providerId}`} />)
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Alert>
      </DialogContent>

      <DialogFooter>
        <Button
          {...{ 'aria-label': 'duplicate-map-confirm-button' }}
          variant="outlined"
          onClick={duplicateMap}
          loading={isDuplicating}
          color="primary"
        >
          <FormattedMessage id="builder.actions.duplicate.dialog.continue" />
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

export default PartiallyDuplicateMapDialog
